import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Source, sourceFromLocation } from './api/client';
import { type Me, signOut, whoAmI } from './credential/api';
import { pageFor } from './credential/page';
import { SIGNATORY_CHANGED_SENTENCE, signatoryChanged } from './credential/session';
import { AssessmentPage } from './components/AssessmentPage';
import { Landing } from './components/Landing';
import { SignInPage } from './components/credential/SignInPage';
import { InvitationPage } from './components/credential/InvitationPage';
import { ResetPage } from './components/credential/ResetPage';

/**
 * The console: three credential pages, the landing page, and the assessment
 * page, chosen by path and then by query (docs/DECISIONS.md ZD-118, ZD-124).
 *
 * The three paths are the ones `sds-13a` decided in `@zevik/schemas`, because
 * an emailed link outlives the deployment that sent it; every other path is
 * the console. There is no router library: `pageFor` is one lookup, the
 * query string says which assessment (`?assessment=`) or none, and a
 * credential page never navigates except by a full load, which is the only
 * kind of navigation a cookie set by the API needs.
 */
export function App(): React.JSX.Element {
  const page = useMemo(() => pageFor(window.location.pathname), []);
  if (page === 'sign-in') {
    return (
      <SignInPage
        onSignedIn={() => {
          window.location.assign('/');
        }}
      />
    );
  }
  if (page === 'invitation') return <InvitationPage />;
  if (page === 'reset') return <ResetPage />;
  return <Console />;
}

/**
 * The signed-in console: who is reading, and what happens when the session
 * ends under them (`credential/session.ts`).
 *
 * With no `?assessment=` the person lands on the tenant's assessments
 * (ZD-120 §2) — the page that did not exist when a fresh sign-in met a 404.
 * With one, the assessment page reads `next` and shows the screen that
 * serves it. `?fixture` is the review screen over the committed fixture,
 * whichever id is beside it.
 *
 * Why a sign-in is being asked for, or `null`: `load` is a 401 before
 * anything was read, so the sign-in page stands in for the page; `record` is
 * a 401 on an act, so the page stays exactly as it was — draft included —
 * with the form above it. After a sign-in from either, find out who signed in
 * **before** reading anything else, and drop every draft if it is not the
 * person who was here.
 */
function Console(): React.JSX.Element {
  const source = useMemo<Source>(() => sourceFromLocation(window.location.search), []);
  const [me, setMe] = useState<Me | null>(null);
  const [signInFor, setSignInFor] = useState<{ why: 'load' | 'record'; reason: string } | null>(
    null,
  );
  const [notice, setNotice] = useState<string | null>(null);
  /** Bumped to remount the review screen — which drops every draft in it. */
  const [generation, setGeneration] = useState(0);
  /** Every successful sign-in from the panel, so a stale refusal under a draft is cleared. */
  const [sessionRestored, setSessionRestored] = useState(0);
  /** Bumped after a sign-in so a page re-reads the record, which may have moved. */
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    // The fixture has nobody signed in, and says so by never asking.
    if (source.base === null) return;
    let live = true;
    whoAmI(source)
      .then((who) => {
        if (live && who.ok) setMe(who.value);
      })
      .catch(() => {
        // The page's own reads will report the session; this is only the name.
      });
    return () => {
      live = false;
    };
  }, [source]);

  const onSessionLost = useCallback((why: 'load' | 'record', reason: string): void => {
    setSignInFor({ why, reason });
  }, []);

  const onSignedIn = useCallback(async (): Promise<void> => {
    const who = await whoAmI(source);
    if (!who.ok) {
      setSignInFor({ why: signInFor?.why ?? 'load', reason: who.message });
      return;
    }
    if (signatoryChanged(me, who.value)) {
      setGeneration((current) => current + 1);
      setNotice(SIGNATORY_CHANGED_SENTENCE);
    }
    setMe(who.value);
    setSignInFor(null);
    setSessionRestored((current) => current + 1);
    setReloadToken((current) => current + 1);
  }, [source, me, signInFor]);

  const onSignOut = useCallback((): void => {
    void signOut().then((outcome) => {
      if (!outcome.ok) {
        setNotice(outcome.message);
        return;
      }
      // A reload rather than a navigation, so the URL — and with it the
      // assessment, and under `dev-header` the stand-in's claims — survives
      // into the sign-in the page will now show in its place.
      window.location.reload();
    });
  }, []);

  if (source.base === null || source.assessmentId !== null) {
    return (
      <AssessmentPage
        source={source}
        assessmentId={source.assessmentId ?? 'assessment_vetchford'}
        me={me}
        onSignOut={onSignOut}
        signInFor={signInFor}
        onSessionLost={onSessionLost}
        onSignedIn={onSignedIn}
        notice={notice}
        generation={generation}
        sessionRestored={sessionRestored}
        reloadToken={reloadToken}
      />
    );
  }

  if (signInFor !== null) {
    return <SignInPage reason={signInFor.reason} onSignedIn={onSignedIn} />;
  }

  return (
    <Landing
      key={reloadToken}
      source={source}
      me={me}
      onSignOut={onSignOut}
      onSessionLost={(reason) => {
        onSessionLost('load', reason);
      }}
    />
  );
}
